import { ADVERTISING, CHANNELS, cleanSpecField, FORMATS, INCLUDES, MAX_DELIVERY_DAYS, SUBTITLE_LANGS, type DeliverySpec, type SpecKey } from './spec.js';

/**
 * Klara's suggestions for a project's delivery spec from the client's meetings (owner 2026-11-01, thread "Matcha
 * Mondays projektdetaljer i Hubben": "Skulle man kunna att saker fylls i utifrån möten? eller ges förslag"). Klara only
 * suggests fields that are still empty, each with the words from the meeting it rests on; the producer picks which to
 * use and nothing is saved until they do. The invoice reference and the planning date are left to the producer.
 */
export const SUGGEST_SPEC_KEYS = ['lengths', 'formats', 'channels', 'subtitles', 'advertising', 'deliveryDays', 'includes', 'untilMonth'] as const satisfies readonly SpecKey[];
export type SuggestSpecKey = (typeof SUGGEST_SPEC_KEYS)[number];

export interface SpecSuggestion<K extends SuggestSpecKey = SuggestSpecKey> {
  key: K;
  value: DeliverySpec[K];
  /** the words in the meeting the value rests on */
  quote: string;
}

/** The spec's empty fields that Klara may suggest; subtitles "Nej" ([]) counts as set. */
export function emptySuggestKeys(spec: DeliverySpec): SuggestSpecKey[] {
  return SUGGEST_SPEC_KEYS.filter((k) => {
    const v = spec[k];
    if (k === 'subtitles') return v === null;
    return Array.isArray(v) ? v.length === 0 : v === null;
  });
}

const FIELD_HELP: Record<SuggestSpecKey, string> = {
  lengths: 'number of films and their lengths, as one short line ("3 films à 60 s + 6 short cut-downs")',
  formats: `list of aspect ratios; use ${FORMATS.join(', ')} when they fit, else a short custom one`,
  channels: `list of where the films are published; use the keys ${CHANNELS.join(', ')} (fair = trade fair, tv = TV or streaming) when they fit, else a short custom one`,
  subtitles: `list of subtitle languages as keys ${SUBTITLE_LANGS.join(', ')} (or the language's name); [] when the client said no subtitles`,
  advertising: `one of ${ADVERTISING.join(', ')}: organic posts, paid ads, or both`,
  deliveryDays: `whole number of working days from the shoot to the first draft (1–${MAX_DELIVERY_DAYS}), only when a time was agreed`,
  includes: `list of what the delivery contains beyond the films, keys only: ${INCLUDES.join(', ')} (photo = stills, ai = AI-generated material, actor = actors, voice = voice-over)`,
  untilMonth: 'the last month (YYYY-MM) when the project runs over several months'
};

/** The system prompt for these fields; {TODAY} is filled in by the caller. */
export function specSuggestPrompt(keys: readonly SuggestSpecKey[], today: string): string {
  return `You read the meeting notes between Klara Färdiga Film (KFF, a Swedish video production company) and a client, and
pick out what was said about how the films will be delivered. Today is ${today}.
Answer with one JSON object and nothing else: {"suggestions": [{"key": ..., "value": ..., "quote": ...}]}.
Only these keys, each at most once:
${keys.map((k) => `- "${k}": ${FIELD_HELP[k]}`).join('\n')}
"quote" is the short sentence from the notes that says it, word for word (at most 200 characters).
Only suggest what the notes clearly say about this project – never guess, never fill in a usual value. Newer meetings
win over older ones. Leave out a key the notes say nothing about; an empty list of suggestions is a fine answer.`;
}

/** The model's answer, read leniently: unknown keys, values that are not choices and keys without a quote are dropped. */
export function parseSpecSuggestions(text: string, allowed: readonly SuggestSpecKey[]): SpecSuggestion[] {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(text.slice(start, end + 1));
  } catch {
    return [];
  }
  const list = raw && typeof raw === 'object' ? (raw as { suggestions?: unknown }).suggestions : null;
  if (!Array.isArray(list)) return [];
  const out: SpecSuggestion[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const { key, value, quote } = item as Record<string, unknown>;
    if (typeof key !== 'string' || !(allowed as readonly string[]).includes(key) || out.some((s) => s.key === key)) continue;
    const q = typeof quote === 'string' ? quote.trim().replace(/\s+/g, ' ').slice(0, 200) : '';
    if (!q) continue;
    const k = key as SuggestSpecKey;
    let v: unknown;
    try {
      v = cleanSpecField(k, k === 'subtitles' && Array.isArray(value) ? value.map(languageKey) : value);
    } catch {
      continue;
    }
    // an empty value is no suggestion, except subtitles [] ("no subtitles", which the client can say)
    if (v === null || (Array.isArray(v) && v.length === 0 && k !== 'subtitles')) continue;
    out.push({ key: k, value: v as DeliverySpec[typeof k], quote: q });
  }
  return out;
}

const LANGUAGE_NAMES: Record<string, (typeof SUBTITLE_LANGS)[number]> = {
  svenska: 'sv', swedish: 'sv', engelska: 'en', english: 'en', norska: 'no', norwegian: 'no', danska: 'da', danish: 'da',
  finska: 'fi', finnish: 'fi', tyska: 'de', german: 'de', polska: 'pl', polish: 'pl', franska: 'fr', french: 'fr', arabiska: 'ar', arabic: 'ar'
};
/** "Swedish" or "svenska" into the key; anything else as it was. */
const languageKey = (v: unknown) => (typeof v === 'string' ? (LANGUAGE_NAMES[v.trim().toLowerCase()] ?? v) : v);
